import React, { useEffect, useMemo, useRef } from "react"

import { cn } from "../../utils/cn.ts"
import { prepareRichTextForDisplay } from "./rich-text-html.ts"

import "./rich-text-editor.css"

interface ResolvedAttachment {
  url: string
  name?: string
}

interface RichTextViewerProps {
  html?: string | null
  className?: string
  /** Looks an attachment chip's id up against the entity's *current*
   * attachment list — never trust a URL frozen inside old saved HTML. */
  resolveAttachment?: (id: string) => ResolvedAttachment | null | undefined
  unavailableLabel?: string
}

function RichTextViewer({
  html,
  className,
  resolveAttachment,
  unavailableLabel
}: RichTextViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sanitized = useMemo(() => prepareRichTextForDisplay(html), [html])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const chips =
      container.querySelectorAll<HTMLElement>("[data-attachment-id]")

    chips.forEach((chip) => {
      const id = chip.getAttribute("data-attachment-id")
      const resolved = id ? resolveAttachment?.(id) : null

      chip.classList.toggle("is-resolved", !!resolved)
      chip.classList.toggle("is-unavailable", !resolved)
      chip.title = resolved ? "" : (unavailableLabel ?? "")
      chip.onclick = resolved
        ? () => window.open(resolved.url, "_blank", "noopener")
        : null
    })
  }, [sanitized, resolveAttachment, unavailableLabel])

  return (
    <div
      ref={containerRef}
      className={cn("rich-text-content whitespace-normal break-words", className)}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  )
}

export { type RichTextViewerProps, RichTextViewer }
